import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useCollectionsQueryOptions } from "@-/dev.improve.haniverse.lib.collection.list";
import {
  addMorphemeToCollection,
  useCollectionMorphemeQueryOptions,
} from "@-/dev.improve.haniverse.lib.collection.morpheme";
import { useMutation, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMemo } from "react";

/**
 * The reader's collections (Favorites first, then oldest first), each marked with whether the panel
 * character is already in it, plus an action that adds the character to one. Shared by the toolbar
 * dropdown and the native card actions menu.
 */
export function useAddMorphemeToCollection() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const [morpheme] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
  );
  const { data: collectionListData } = useQuery(useCollectionsQueryOptions());
  const { data: collectionMorphemeListData } = useQuery(
    useCollectionMorphemeQueryOptions(morpheme),
  );

  const collections = useMemo(() => {
    const collectionMembershipIdSet = new Set(
      (collectionMorphemeListData ?? []).map(
        (collectionMorpheme) => collectionMorpheme.collectionId,
      ),
    );
    return [...(collectionListData ?? [])]
      .sort((a, b) => {
        if (a.isFavorites && !b.isFavorites) {
          return -1;
        }
        if (!a.isFavorites && b.isFavorites) {
          return 1;
        }
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      })
      .map((collection) => ({
        id: collection.id,
        isFavorites: collection.isFavorites,
        isMember: collectionMembershipIdSet.has(collection.id),
        name: collection.name,
      }));
  }, [collectionListData, collectionMorphemeListData]);

  const addToCollectionMutation = useMutation({
    mutationFn: async (collectionId: string): Promise<void> => {
      await actorDb(import.meta.location).transaction(async (tx) =>
        addMorphemeToCollection(tx, { collectionId, morpheme, nowIso: new Date().toISOString() }),
      );
    },
    mutationKey: ["addMorphemeToCollection"],
  });

  return {
    add: (collectionId: string) => addToCollectionMutation.mutate(collectionId),
    collections,
    isPending: addToCollectionMutation.isPending,
  };
}
